import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Material
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Services
import { NotificationsService } from 'shared/services/notifications.service';

// Shared
import { DbNotification, NotificationPriority, NotificationType } from 'fakturnia-shared';
import { DateTime } from 'luxon';
import { DateFormatterComponent } from '../date-formatter/date-formatter.component';

@Component({
    selector: 'notifications-bell',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatBadgeModule,
        MatMenuModule,
        MatTooltipModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        DateFormatterComponent
    ],
    templateUrl: './notifications-bell.component.html',
    styleUrls: ['./notifications-bell.component.scss']
})
export class NotificationsBellComponent implements OnInit, OnDestroy {
    private _destroy$ = new Subject<void>();

    @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

    notifications: DbNotification[] = [];
    unreadCount: number = 0;
    isLoading: boolean = false;

    // Pokazuj max 10 powiadomień w dropdown
    readonly MAX_DISPLAY = 10;

    constructor(
        private _notificationsService: NotificationsService,
        private _router: Router
    ) {}

    ngOnInit(): void {
        // // === TESTOWE POWIADOMIENIA (DO USUNIĘCIA) ===
        // this.notifications = [
        //     {
        //         _id: 'test-1',
        //         userId: 'test',
        //         type: NotificationType.KSEF_EXPENSE_INVOICE,
        //         priority: NotificationPriority.HIGH,
        //         title: 'Nowa faktura kosztowa',
        //         message: 'Otrzymano fakturę FV/2026/01/0042 od ABC Sp. z o.o. na kwotę 1 230,00 PLN brutto',
        //         icon: 'tabler_outline:file-invoice',
        //         isRead: false,
        //         isDeleted: false,
        //         createdAt: new Date().toISOString(),
        //         metadata: {
        //             ksefNumber: '1234567890-20260120-ABC123DEF456-00',
        //             invoiceNumber: 'FV/2026/01/0042',
        //             amount: 1230.00,
        //             currency: 'PLN',
        //             contractorName: 'ABC Sp. z o.o.',
        //             nip: '1234567890'
        //         }
        //     },
        //     {
        //         _id: 'test-2',
        //         userId: 'test',
        //         type: NotificationType.KSEF_EXPENSE_INVOICE,
        //         priority: NotificationPriority.NORMAL,
        //         title: 'Nowa faktura kosztowa',
        //         message: 'Otrzymano fakturę FV/123/2026 od XYZ S.A. na kwotę 5 500,00 PLN brutto',
        //         icon: 'tabler_outline:file-invoice',
        //         isRead: true,
        //         isDeleted: false,
        //         createdAt: new Date(Date.now() - 3600000).toISOString(), // 1h temu
        //         metadata: {
        //             ksefNumber: '9876543210-20260120-XYZ789GHI012-00',
        //             invoiceNumber: 'FV/123/2026',
        //             amount: 5500.00,
        //             currency: 'PLN',
        //             contractorName: 'XYZ S.A.',
        //             nip: '9876543210'
        //         }
        //     },
        //     {
        //         _id: 'test-3',
        //         userId: 'test',
        //         type: NotificationType.KSEF_INCOME_INVOICE,
        //         priority: NotificationPriority.LOW,
        //         title: 'Faktura wysłana do KSeF',
        //         message: 'Faktura FV/2026/01/0015 została pomyślnie wysłana do KSeF',
        //         icon: 'tabler_outline:check',
        //         isRead: false,
        //         isDeleted: false,
        //         createdAt: new Date(Date.now() - 86400000).toISOString(), // wczoraj
        //         metadata: {
        //             invoiceNumber: 'FV/2026/01/0015',
        //             amount: 3200.00,
        //             currency: 'PLN'
        //         }
        //     }
        // ] as DbNotification[];
        // this.unreadCount = this.notifications.filter(n => !n.isRead).length;
        // return; // STOP - testowe dane
        // // === KONIEC TESTOWYCH POWIADOMIEŃ ===

        // Subskrybuj powiadomienia
        this._notificationsService.notifications$
            .pipe(takeUntil(this._destroy$))
            .subscribe(notifications => {
                this.notifications = notifications.slice(0, this.MAX_DISPLAY);
            });

        // Subskrybuj licznik nieprzeczytanych
        this._notificationsService.unreadCount$
            .pipe(takeUntil(this._destroy$))
            .subscribe(count => {
                this.unreadCount = count;
            });

        // Subskrybuj loading state
        this._notificationsService.isLoading$
            .pipe(takeUntil(this._destroy$))
            .subscribe(loading => {
                this.isLoading = loading;
            });

        // Dane ładowane z lokalnej bazy automatycznie przez serwis
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    /**
     * Otwiera menu i odświeża dane
     */
    onMenuOpened(): void {
        // Można dodać odświeżanie przy otwarciu
    }

    /**
     * Oznacza powiadomienie jako przeczytane i nawiguje do dokumentu/akcji
     */
    async onNotificationClick(notification: DbNotification): Promise<void> {
        console.log(notification)
        if (!notification.isRead) {
            await this._notificationsService.markAsRead(notification._id);
        }

        // Zamknij menu
        this.menuTrigger?.closeMenu();

        // Nawigacja - priorytet: documentId > actionUrl
        if (notification.metadata?.documentId) {
            // Przekierowanie do podglądu dokumentu/faktury
            this._router.navigate(['/konto/dokumenty', notification.metadata.documentId, 'podglad']);
        } else if (notification.metadata?.actionUrl) {
            // Nawigacja do custom URL
            this._router.navigateByUrl(notification.metadata.actionUrl);
        }
    }

    /**
     * Oznacza wszystkie jako przeczytane
     */
    async markAllAsRead(): Promise<void> {
        await this._notificationsService.markAllAsRead();
    }

    /**
     * Usuwa powiadomienie
     */
    async deleteNotification(event: Event, notification: DbNotification): Promise<void> {
        event.stopPropagation();
        await this._notificationsService.deleteNotification(notification._id);
    }

    /**
     * Formatuje czas relatywnie (np. "5 minut temu")
     */
    formatRelativeTime(dateStr: string): string {
        const date = DateTime.fromISO(dateStr);
        const diff = date.diffNow(['days', 'hours', 'minutes']);

        if (Math.abs(diff.days) >= 1) {
            return date.toFormat('dd.MM.yyyy HH:mm');
        } else if (Math.abs(diff.hours) >= 1) {
            const hours = Math.abs(Math.round(diff.hours));
            return `${hours} ${hours === 1 ? 'godzinę' : hours < 5 ? 'godziny' : 'godzin'} temu`;
        } else {
            const minutes = Math.max(1, Math.abs(Math.round(diff.minutes)));
            return `${minutes} ${minutes === 1 ? 'minutę' : minutes < 5 ? 'minuty' : 'minut'} temu`;
        }
    }

    /**
     * Pobiera ikonę dla typu powiadomienia
     */
    getIcon(notification: DbNotification): string {
        return notification.icon || this._notificationsService.getIconForType(notification.type);
    }

    /**
     * Pobiera klasę koloru dla priorytetu
     */
    getColorClass(notification: DbNotification): string {
        return this._notificationsService.getColorClassForPriority(notification.priority);
    }

    /**
     * Pobiera klasę tła ikonki dla priorytetu
     */
    getIconBgClass(notification: DbNotification): string {
        const bgClasses: Record<NotificationPriority, string> = {
            [NotificationPriority.LOW]: 'icon-bg-low',
            [NotificationPriority.NORMAL]: 'icon-bg-normal',
            [NotificationPriority.HIGH]: 'icon-bg-high',
            [NotificationPriority.CRITICAL]: 'icon-bg-critical'
        };
        return bgClasses[notification.priority] || 'icon-bg-normal';
    }

    /**
     * Pobiera klasę tła dla priorytetu
     */
    getBgClass(notification: DbNotification): string {
        if (!notification.isRead) {
            return this._notificationsService.getBgClassForPriority(notification.priority);
        }
        return '';
    }

    /**
     * Sprawdza czy powiadomienie ma wysoki/krytyczny priorytet
     */
    isHighPriority(notification: DbNotification): boolean {
        return notification.priority === NotificationPriority.HIGH || 
               notification.priority === NotificationPriority.CRITICAL;
    }
}
